import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModal, LanguageSelector } from 'app/components';
import { langs } from 'shared/forms/LanguageSelector';
import { i18n } from 'shared/i18n';
import { useDocumentParams } from 'app/router/params';

import { useEditorDocumentQuery, useUpdateDocumentLocale } from 'entities/document';
import { useSetEditorDocument } from 'app/api/documents/useEditorData';

const Locale = () => {
  const { t } = useTranslation('project-details');
  const setEditorDocument = useSetEditorDocument();
  const { documentId } = useDocumentParams();
  const { data: document } = useEditorDocumentQuery({ documentId });

  const { mutate: updateLocale, isPending } = useUpdateDocumentLocale();
  const [pendingChange, setPendingChange] = useState<{ from: string; to: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!document) {
    return null;
  }

  const currentLocale = document.content?.locale || 'sv-SE';

  const localeName = (locale: string) => {
    const entry = langs.find((lang) => lang.locale === locale);
    return entry ? i18n(entry.labelKey) : locale;
  };

  const handleChange = (lang: string) => {
    if (lang === currentLocale) return;
    setPendingChange({ from: currentLocale, to: lang });
    setShowConfirm(true);
  };

  const closeConfirm = () => setShowConfirm(false);

  const handleConfirm = () => {
    if (!pendingChange) return;
    updateLocale(
      {
        documentId,
        revisionId: document.revisionId,
        superId: document.superId,
        locale: pendingChange.to,
      },
      {
        onSuccess: (updated) => {
          setEditorDocument(updated);
          closeConfirm();
        },
        onError: closeConfirm,
      }
    );
  };

  return (
    <div className="w-25">
      <LanguageSelector
        sm
        onChange={handleChange}
        locale={currentLocale}
        {...({ className: 'tippy-box' } as ComponentProps<typeof LanguageSelector> & { className?: string })}
      />
      <ConfirmationModal
        show={showConfirm}
        busy={isPending}
        onConfirm={handleConfirm}
        onDecline={closeConfirm}
        message={
          pendingChange && (
            <>
              <i className="fa-regular fa-triangle-exclamation fs-1 mb-4" />
              <h3>{t('change-locale.heading')}</h3>
              <p className="text-gray-900 fs-7">{t('change-locale.warning', { from: localeName(pendingChange.from), to: localeName(pendingChange.to) })}</p>
              <span className="text-gray-900 fs-7">{t('change-locale.explanation')}</span>
            </>
          )
        }
      />
    </div>
  );
};

export default Locale;
